import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import {
  ICreateDepollutionRequest,
  ICreateUpdateDepollutionItemRequest,
  IDepollutionByVehicleResponse,
  IDepollutionItemsResponse,
  IDepollutionStepsResponse,
  ISingleDepollutionResponse,
} from "../models/Depollution";

export default function useDepollution(depollutionId?: string, vehicleId?: string) {
  const { get, post } = useApiHelper();
  const queryClient = useQueryClient();

  const depollution = useQuery({
    queryKey: ["depollution", depollutionId],
    queryFn: () => get<ISingleDepollutionResponse>(`/depollution/${depollutionId}`),
    enabled: !!depollutionId,
    staleTime: 1000 * 60 * 2,
  });

  const depollutionByVehicle = useQuery({
    queryKey: ["depollution-vehicle", vehicleId],
    queryFn: () => get<IDepollutionByVehicleResponse>(`/depollution/vehicle/${vehicleId}`),
    enabled: false,
    staleTime: 1000 * 60 * 2,
    retry: false,
  });

  const depollutionItems = useQuery({
    queryKey: ["depollution-items", depollutionId],
    queryFn: () => get<IDepollutionItemsResponse>(`/depollution/${depollutionId}/items`),
    enabled: !!depollutionId,
    staleTime: 1000 * 60,
  });

  const depollutionSteps = useQuery({
    queryKey: ["depollution-steps"],
    queryFn: () => get<IDepollutionStepsResponse>(`/depollution/steps?activeOnly=true`),
    staleTime: 1000 * 60 * 10,
  });

  const createDepollution = useMutation(
    (body: ICreateDepollutionRequest) => post<ICreateDepollutionRequest, ISingleDepollutionResponse>("/depollution", body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["depollution", depollutionId]);
        queryClient.invalidateQueries(["depollution-vehicle", vehicleId]);
        queryClient.invalidateQueries(["vehicles", vehicleId]);
        queryClient.invalidateQueries(["pagedTable", "/depollution/filtered"]);
      },
    }
  );

  const saveDepollutionItem = useMutation(
    (body: ICreateUpdateDepollutionItemRequest) => post("/depollution/items", body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["depollution", depollutionId]);
        queryClient.invalidateQueries(["depollution-items", depollutionId]);
        queryClient.invalidateQueries(["depollution-vehicle"]);
        queryClient.invalidateQueries(["vehicles", vehicleId]);
        queryClient.invalidateQueries(["pagedTable", "/depollution/filtered"]);
      },
    }
  );

  return {
    depollution,
    depollutionByVehicle,
    depollutionItems,
    depollutionSteps,
    createDepollution,
    saveDepollutionItem,
  };
}
